import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Download, Loader2, TrendingUp } from "lucide-react";

// Illustrative ROI model — MIRRORS backend routers/roi.py::compute_roi verbatim
// so the on-screen and PDF figures always match. Keep both in sync.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round1k = (x) => Math.round(x / 1000) * 1000;

function computeRoi(revenue, suppliers, incidents) {
  const r = clamp(Number(revenue) || 0, 0, 1e12);
  const s = Math.trunc(clamp(Number(suppliers) || 0, 0, 100000));
  const n = Math.trunc(clamp(Number(incidents) || 0, 0, 1000));
  const exposurePer = clamp(r * 0.0008, 75000, 4000000);
  const supplyFactor = 1 + (Math.min(s, 2000) / 2000) * 0.5;
  const gross = n * exposurePer * supplyFactor;
  const lossAvoidance = gross * 0.3;
  const efficiency = clamp(s * 120, 25000, 250000);
  const annual = lossAvoidance + efficiency;
  return {
    revenue: r, suppliers: s, incidents: n,
    exposure_per_incident: round1k(exposurePer),
    gross_exposure: round1k(gross),
    loss_avoidance: round1k(lossAvoidance),
    efficiency_gain: round1k(efficiency),
    annual_value: round1k(annual),
  };
}

const gbp = (x) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(x || 0);

// Pure presentational row — defined outside the parent so React keeps the
// same component identity across renders (no DOM churn / wiped local state).
const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between border-b border-black/5 py-2.5 text-sm last:border-0">
    <span className="text-ek-ink/70">{label}</span>
    <span className={accent ? "font-bold text-ek-purple" : "font-semibold text-ek-ink"}>{gbp(value)}</span>
  </div>
);

export function RoiCalculator({ onSummary }) {
  const [revenue, setRevenue] = useState(500000000);
  const [suppliers, setSuppliers] = useState(250);
  const [incidents, setIncidents] = useState(6);
  const [company, setCompany] = useState("");
  // Optional nurture-email capture. Both fields stay empty by default so
  // the no-friction "just see the number" path is preserved — only if
  // the visitor fills these in do we trigger the role-tailored nurture
  // email (with the ROI one-pager + sample Maturity radar attached).
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  const [downloading, setDownloading] = useState(false);

  const roi = useMemo(() => computeRoi(revenue, suppliers, incidents), [revenue, suppliers, incidents]);

  // Surface a one-line summary to the parent so the pilot lead form can attach it.
  // Must be a side effect (not useMemo) — calling onSummary during render triggers
  // a parent setState in the middle of a child render, which React 19 warns about.
  useEffect(() => {
    onSummary?.(
      `ROI estimate — est. annual value ${gbp(roi.annual_value)} ` +
      `(revenue ${gbp(roi.revenue)}, ${roi.suppliers} suppliers, ${roi.incidents} incidents/yr; ` +
      `loss avoidance ${gbp(roi.loss_avoidance)} + efficiency ${gbp(roi.efficiency_gain)}).`,
    );
  }, [roi, onSummary]);

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.post(
        "/roi/one-pager",
        { revenue, suppliers, incidents, company, email, role, name },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ERIO-ROI-Estimate.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const numField =
    "w-full rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm text-ek-ink outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20";

  return (
    <section className="mx-auto max-w-6xl px-6 pb-20" data-testid="roi-calculator">
      <div className="rounded-2xl border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.05] to-transparent p-7 lg:p-10">
        <div className="flex items-center gap-2">
          <TrendingUp className="text-ek-purple" size={18} />
          <span className="text-xs font-semibold uppercase tracking-wider text-ek-purple">Estimate your return</span>
        </div>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">What could ERIO be worth to you?</h2>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
          A transparent, deliberately conservative estimate on your own figures. Adjust the inputs to see the
          illustrative annual value — then download the one-pager or attach it to a pilot enquiry.
        </p>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/70">Annual revenue (£)</label>
              <input type="number" min="0" step="1000000" value={revenue}
                onChange={(e) => setRevenue(e.target.value)} className={numField} data-testid="roi-revenue" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-ek-ink/70">Suppliers</label>
                <input type="number" min="0" value={suppliers}
                  onChange={(e) => setSuppliers(e.target.value)} className={numField} data-testid="roi-suppliers" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ek-ink/70">Risk incidents / year</label>
                <input type="number" min="0" value={incidents}
                  onChange={(e) => setIncidents(e.target.value)} className={numField} data-testid="roi-incidents" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ek-ink/70">Company (optional, for the PDF)</label>
              <input value={company} onChange={(e) => setCompany(e.target.value)} className={numField}
                placeholder="Acme plc" data-testid="roi-company" />
            </div>
            {/* Optional nurture-email capture. Empty by default so the
                "just see the number" path remains zero-friction. Filling
                these triggers a role-tailored nurture email with the ROI
                one-pager + a sample Maturity radar attached. */}
            <details className="rounded-lg border border-black/10 bg-white/60 p-3" data-testid="roi-nurture-toggle">
              <summary className="cursor-pointer text-xs font-semibold text-ek-ink/75">
                Want the PDF + a sample Maturity radar emailed to you?
                <span className="ml-1 font-normal text-ek-ink/55">(optional)</span>
              </summary>
              <div className="mt-3 grid gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-ek-ink/70">Your name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className={numField}
                    placeholder="Alex Morgan" data-testid="roi-name" autoComplete="name" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ek-ink/70">Work email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={numField}
                    placeholder="alex@acme.com" data-testid="roi-email" autoComplete="email" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ek-ink/70">Your role</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)} className={numField}
                          data-testid="roi-role">
                    <option value="">Select…</option>
                    <option value="CRO">Chief Risk Officer</option>
                    <option value="CISO">CISO</option>
                    <option value="PMO Director">PMO Director</option>
                    <option value="Transformation Director">Transformation Director</option>
                    <option value="CFO">CFO</option>
                    <option value="CEO">CEO</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <p className="text-[11px] leading-relaxed text-ek-ink/55">
                  We&apos;ll send a single role-tailored email with the ROI PDF and a
                  sample Maturity radar attached. No newsletter, no follow-up sequence —
                  reply if you&apos;d like to chat.
                </p>
              </div>
            </details>
            <button onClick={download} disabled={downloading}
              className="ek-btn flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold"
              data-testid="roi-download">
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Download ROI one-pager (PDF)
            </button>
          </div>

          {/* Result */}
          <div className="rounded-xl border border-black/5 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-ek-purple">Estimated annual value</p>
            <p className="mt-1 text-4xl font-extrabold tracking-tight text-ek-ink" data-testid="roi-annual-value">
              {gbp(roi.annual_value)}
            </p>
            <div className="mt-5">
              <Row label="Exposure per material incident" value={roi.exposure_per_incident} />
              <Row label="Gross annual risk exposure" value={roi.gross_exposure} />
              <Row label="Loss avoidance · ~30% earlier detection" value={roi.loss_avoidance} />
              <Row label="Risk-team efficiency gain" value={roi.efficiency_gain} />
              <Row label="Estimated annual value" value={roi.annual_value} accent />
            </div>
            <p className="mt-4 text-[0.7rem] leading-relaxed text-ek-ink/65">
              Illustrative only, from your figures and ERIO&apos;s published assumptions. Not a forecast or guarantee.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
